import { useFeatureFlagsQuery } from "../featureFlags.query";

export const getFabricFeatureFlagAccessError = ({
  key,
  ignoreUserId = false,
  ignoreOperationId = false,
}: {
  key: string;
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
}) => {
  let modifiersString = "";
  if (ignoreUserId && ignoreOperationId) {
    modifiersString = " without userId and operationId";
  } else if (ignoreUserId) {
    modifiersString = " without userId";
  } else if (ignoreOperationId) {
    modifiersString = " without operationId";
  }

  return new Error(
    `Attempted to access fabric feature flag "${key}"${modifiersString} but it is not available`,
  );
};

// ###############################
// ## useMaybeFabricFeatureFlag ##
// ###############################

export type ParamsOfUseMaybeFabricFeatureFlag = {
  key: string;
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseMaybeFabricFeatureFlag = boolean | undefined;
export type UseMaybeFabricFeatureFlag = (
  params: ParamsOfUseMaybeFabricFeatureFlag,
) => ResultOfUseMaybeFabricFeatureFlag;

/**
 * The result from this hook will be undefined or
 * the boolean value of the fabric feature flag matching `key`,
 * taking into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flag will not target any user.
 * Otherwise, the feature flag will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flag will not target any operation.
 * Otherwise, the feature flag will target the current `operationId`.
 *
 * This hook is guaranteed to contain data or will throw.
 */
export const useMaybeFabricFeatureFlag: UseMaybeFabricFeatureFlag = ({
  key,
  ignoreUserId = false,
  ignoreOperationId = false,
}) => {
  const { data } = useFeatureFlagsQuery({
    app: "fabric",
    ignoreUserId,
    ignoreOperationId,
    watch: ["data"],
  });
  return data?.featureFlagsAsBooleansByKey[key];
};

// ##########################
// ## useFabricFeatureFlag ##
// ##########################

export type ParamsOfUseFabricFeatureFlag = {
  key: string;
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseFabricFeatureFlag = boolean;
export type UseFabricFeatureFlag = (
  params: ParamsOfUseFabricFeatureFlag,
) => ResultOfUseFabricFeatureFlag;

/**
 * The result from this hook will be
 * the boolean value of the fabric feature flag matching `key`,
 * taking into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flag will not target any user.
 * Otherwise, the feature flag will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flag will not target any operation.
 * Otherwise, the feature flag will target the current `operationId`.
 *
 * This hook is guaranteed to contain data or will throw.
 */
export const useFabricFeatureFlag: UseFabricFeatureFlag = ({
  key,
  ignoreUserId = false,
  ignoreOperationId = false,
}) => {
  const data = useMaybeFabricFeatureFlag({
    key,
    ignoreUserId,
    ignoreOperationId,
  });
  if (data === undefined) {
    throw getFabricFeatureFlagAccessError({
      key,
      ignoreUserId,
      ignoreOperationId,
    });
  }
  return data;
};
